import { Location } from '@angular/common';
import { Observable, Subject, combineLatest, ReplaySubject } from 'rxjs';
import { SapiEntity } from './../../../services/sapi.service';
import { PublicationService } from './../../../services/publication.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { APIKey } from './../../../interfaces/user';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { ClipboardService } from 'ngx-clipboard';
import { debounceTime } from 'rxjs/operators';

interface Role {
	id: string;
	name: string;
	label?: string;
	checked: boolean;
	permissions: string[];
	help?: string;
	disabled?: boolean;
}
@Component({
	selector: 'api-key-details',
	templateUrl: './api-key-details.component.html',
	styleUrls: ['./api-key-details.component.scss']
})

export class ApiKeyDetailsComponent extends AbstractDetailsComponent implements OnInit {
	apikey: APIKey;
	activeTab: string;
	id: string;
	isNew: boolean;
	roles: Role[];
	roleNames: string[];
	extraRoles: Role[];
	extraRoleNames: string[];
	repeat_password: string;
	passwordMatch: boolean;
	putRight: boolean;
	apiSecret = '';
	legacyRoles = false;
	legacyRolesInitial: boolean;

	userSubject: ReplaySubject<APIKey>;

	constructor(
		protected route: ActivatedRoute,
		protected location: Location,
		private publicationService: PublicationService,
		private clipboardService: ClipboardService,
		private formBuilder: UntypedFormBuilder) {

		super(route, location);

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.userSubject = new ReplaySubject<APIKey>(1);

		this.entityConfiguration = {
			name: 'apikey',
			parentPath: '/publication/api-keys',
			basePath: '/publication/api-keys/',
			defaults: {
				label: '',
				description: '',
				roles: [],
				status: 'active'
			} as APIKey
		};

		this.formGroup = new UntypedFormGroup({
			label: new UntypedFormControl('', { validators: Validators.required, updateOn: 'change' }),
			description: new UntypedFormControl(),
			secret: new UntypedFormControl(),
			id: new UntypedFormControl(),
			createddate: new UntypedFormControl(),
			updateddate: new UntypedFormControl(),
			status: new UntypedFormControl(),
			roles: new UntypedFormArray([])
		},
		{ updateOn: 'blur' }
		);

		this.roleNames = ['ovp-analyst', 'ovp-editor', 'ovp-publisher', 'ovp-administrator'];
	}

	ngOnInit() {
		super.ngOnInit();
		this.apikey = { ...this.entity } as APIKey;
		this.subscriptions.add(
			this.formGroup.valueChanges.pipe(
				debounceTime(500))
				.subscribe(newValue => this.checkValues(false, newValue))
		);

		if (this.isNew) {
			this.userSubject.next(this.entityConfiguration.defaults);
		}

		this.subscriptions.add(
			combineLatest([this.publicationService.getRoles(), this.userSubject]).subscribe(([roles, apikey]: any) => {
				if (this.isNew) {
					if (roles) {
						this.extraRoleNames = roles.filter((role: Role) => role.name && role.name.match(/ovp-\w+-extra/)).map((role: Role) => role.name);
					}
					this.extraRoleNames.sort();
					this.legacyRoles = this.publicationService.hasPublicationField('useLegacyRoles');
					this.legacyRolesInitial = this.legacyRoles;
					this.getRoles();
				} else {
					if (roles) {
						this.extraRoleNames = roles.filter((role: Role) => role.name && role.name.match(/ovp-\w+-extra/)).map((role: Role) => role.name);
					}
					this.extraRoleNames.sort();
					this.legacyRoles = this.hasLegacyRoles(apikey.roles);
					this.legacyRolesInitial = this.legacyRoles;
					this.getRoles();
					this.formGroup.markAsPristine();
				}
			})
		);
	}

	getApiSecret() {
		if (!this.apiSecret) {
			this.sapiService.get('apikey', this.entity.id, 'secret').subscribe((apiSecretResponse: any) => {
				this.apiSecret = apiSecretResponse.body;
			});
		}
	}

	copyToClipboard(str: string, msg: string): void {
		this.clipboardService.copyFromContent(str);
		this.overlayService.createToast(this.translate.instant(msg), 'copy');
	}

	delete(dialogContents?: string): Subject<void> {
		dialogContents = this.translate.instant('DELETE_ENTITY_CONFIRM', {
			entity: this.translate.instant('API_KEY').toLowerCase()
		});
		return super.delete(dialogContents);
	}

	cancel(): Subject<SapiEntity> {
		const cancelSubject = super.cancel();
		cancelSubject.subscribe(u => {
			this.formGroup.reset(u);
			this.getRoles();

			if (this.legacyRoles) {
				this.checkActiveRoles();
			}
		});
		return cancelSubject;
	}

	save(force: boolean = false): Observable<void> {
		this.entity = { ...this.apikey } as SapiEntity;

		this.checkValues(true);
		const saveSubject = super.save(force, null, false);

		saveSubject.subscribe(() => {
			this.apikey = { ...this.entity } as APIKey;

			// Ugly hack to prevent 409 softsave errors...
			this.formGroup.controls.updateddate.setValue(this.apikey.updateddate);

			this.legacyRolesInitial = this.legacyRoles;
		});

		return saveSubject;
	}

	load(id: string, duplicating?: boolean): Observable<SapiEntity> {
		const loadSubject = super.load(id, duplicating);

		loadSubject.subscribe((entity) => {
			const apikey = entity as APIKey;
			this.apikey = { ...apikey } as APIKey;

			this.formGroup.controls.label.setValue(this.apikey.label);
			this.formGroup.controls.description.setValue(this.apikey.description);
			this.formGroup.controls.secret.setValue(this.apikey.secret);
			this.formGroup.controls.id.setValue(this.apikey.id);
			this.formGroup.controls.createddate.setValue(this.apikey.createddate);
			this.formGroup.controls.updateddate.setValue(this.apikey.updateddate);

			const roles = this.formGroup.controls.roles as UntypedFormArray;

			this.apikey.roles.forEach((role: Role) => {
				const formRole = new UntypedFormGroup({
					id: new UntypedFormControl(role.id),
					name: new UntypedFormControl(role.name),
					permissions: this.formBuilder.array(role.permissions)
				});
				roles.push(formRole);
			});

			this.userSubject.next(apikey);

			this.formGroup.markAsPristine();
			this.formGroup.markAsUntouched();
		});

		return loadSubject;
	}

	checkValues(whileSaving: boolean, newValue: any = null) {
		if (this.formGroup && this.formGroup.controls) {
			// at least one role:
			if (this.formGroup.controls.roles) {
				if (!this.formGroup.controls.roles || this.formGroup.controls.roles.value.length === 0) {
					this.formGroup.controls.roles.setErrors({ incorrect: this.translate.instant('SELECT_AT_LEAST_ONE_ROLE') });
				}
			}
		}
	}

	getRoles() {
		this.publicationService.getRoles().subscribe((roles: Role[]) => {
			if (!this.legacyRoles) {
				this.legacyRoles = false;
				this.roles = [];
				this.extraRoles = [];
				let userRoles: Role[] = this.addRolesUsingNames(this.roleNames, roles, this.roles);
				this.roleChanged(userRoles[userRoles.length - 1], true);
				userRoles = this.addRolesUsingNames(this.extraRoleNames, roles, this.extraRoles);
				userRoles.forEach((role: Role) => {
					this.roleChanged(role, true);
				});

			} else {
				this.legacyRoles = true;
				this.extraRoles = null;
				roles = roles.filter((role: Role) => !role.name.startsWith('ovp-'));
				this.roles = roles.sort((a, b) => {
					const textA = a.name.toUpperCase();
					const textB = b.name.toUpperCase();
					return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
				});
				this.checkActiveRoles();
			}
		});
	}

	addRolesUsingNames(roleNames: string[], userRoles: Role[], roles: Role[]): Role[] {
		const rolesForUser: Role[] = [];
		roleNames.forEach((roleName: string) => {
			userRoles.filter((role: Role) => role.name === roleName).map((role: Role) => {
				if (this.apikey.roles.filter((internalRole: Role) => internalRole.name === role.name).length) {
					rolesForUser.push(role);
				}
				role.help = 'HELP.' + role.label.replace(' ', '').toUpperCase() + '_ROLE';
				roles.push({ ...role });
			});
		});

		return rolesForUser;
	}

	hasLegacyRoles(roles: Role[]) {
		let hasLegacyRoles = true;
		roles.forEach((role: Role) => {
			if (this.roleNames.includes(role.name)) {
				hasLegacyRoles = false;
			}
		});
		return hasLegacyRoles;
	}

	checkActiveRoles() {
		const roles = this.formGroup.get('roles') as UntypedFormArray;

		if (roles && roles.controls) {
			this.roles.forEach((role: Role) => {
				const index = roles.controls.findIndex((formRole: UntypedFormControl) => formRole.value.id === role.id);
				role.checked = (index > -1);
			});
		}
	}

	roleChanged(role: Role, enabled: boolean) {
		if (!role) {
			return;
		}
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		let roleIndex = -1;
		let nameIndex = -1;
		let roleName = role.name;

		if (!this.legacyRoles) {
			if ((nameIndex = this.roleNames.indexOf(roleName)) > -1) {
				this.removeRolesFromFormControl(roleName);
				if (!enabled && nameIndex > 0) {
					nameIndex = nameIndex - 1;
					roleName = this.roleNames[nameIndex];
				}
				let index = 0;
				while (index <= nameIndex) {
					const internalRoleName = this.roleNames[index];
					this.roles.filter((internalRole: Role) => internalRole.name === internalRoleName).map((internalRole: Role) => {
						if (enabled) {
							internalRole.checked = true;
						}
						if (internalRole.name === roleName) {
							internalRole.disabled = false;
							this.addRole(internalRole);
						} else {
							internalRole.disabled = true;
						}
					});
					index++;
				}
			} else {
				if (enabled) {
					this.addRole(role);
				} else {
					this.removeRolesFromFormControl(roleName);
				}
				this.extraRoles.forEach((internalRole: Role) => {
					if (internalRole.name === role.name) {
						internalRole.checked = enabled;
					}
				});
			}
		} else {
			roles.controls.forEach((roleFormControl: UntypedFormControl, index) => {
				if (roleFormControl.value && roleFormControl.value.id === role.id) {
					roleIndex = index;
				}
			});
			if (enabled) {
				if (roleIndex === -1) {
					const formRole = new UntypedFormGroup({
						id: new UntypedFormControl(role.id),
						name: new UntypedFormControl(role.name),
						permissions: this.formBuilder.array(role.permissions)
					});
					roles.push(formRole);
				}
			} else if (roleIndex > -1 && roleIndex < roles.length) {
				roles.removeAt(roleIndex);
			}
		}
		this.formGroup.markAsDirty();
	}

	switchPermission() {
		this.legacyRoles = !this.legacyRoles;
		this.removeRolesFromFormControl();
		if (this.legacyRoles) {
			this.extraRoleNames.forEach((roleName: string) => {
				this.removeRolesFromFormControl(roleName);
			});
		}
		this.getRoles();
		this.formGroup.markAsDirty();
	}

	addRole(role: Role) {
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		const fr = roles.value && roles.value.find((r) => r.name === role.name);
		if (!fr) {
			const formRole = new UntypedFormGroup({
				id: new UntypedFormControl(role.id),
				name: new UntypedFormControl(role.name),
				permissions: this.formBuilder.array(role.permissions)
			});
			roles.push(formRole);
		}
	}

	removeRolesFromFormControl(roleName?: string) {
		const roles: UntypedFormArray = this.formGroup.controls.roles as UntypedFormArray;
		for (let i = roles.length - 1; i >= 0; i--) {
			let roleFormControl = null;
			if (roleName) {
				if (roles.value) {
					roleFormControl = roles.value[i];
				}
				if (roleFormControl && roleFormControl.name === roleName) {
					roles.removeAt(i);
					return;
				}
			} else {
				if (!this.extraRoleNames.includes(roleName)) {
					roles.removeAt(i);
				}
			}
		}
	}
}
